import { useMemo } from 'react';

import type { AssetId, AssetType } from '@infinex/asset-config';
import type { ChainKey } from '@infinex/chain-config';
import type { Collectible } from '@infinex/types';
import { cn } from '@infinex/ui-styles';

import { getChainMetadata } from '../../lib/chainsConstants';
import { CollectibleEmblem } from '../../lib/collectibles';
import { calculateAssetDecimalPrecision } from '../../utils';
import type { AssetIconProps } from '../crates/AssetIcon';
import { AssetIcon } from '../crates/AssetIcon';
import { FormattedPrice } from '../formatted-number';
import { FormattedNumber } from '../formatted-number/FormattedNumber';
import { ProcessingAssetDisplay } from '../transaction-details/ProcessingStatusCard';

export type TransactionAssetDisplayProps = {
  asset: AssetIconProps & {
    assetType: AssetType;
    decimals: number;
    assetGroupId?: AssetId;
    nftIdentifier?: string;
    chain: ChainKey;
  };
  amount: string;
  symbol: string | null | undefined;
  /**
   * When set, the asset is a Collector Crypt card: show the card art + name
   * instead of the mint's token icon/symbol, matching the activity log.
   */
  card?: Collectible | null;

  priceData: { usd: { rate: number } } | undefined;
  className?: string;
};

export const TransactionAssetDisplay = (
  props: TransactionAssetDisplayProps
) => {
  const { asset, symbol, amount, priceData, card, className } = props;

  let assetName = card?.name ?? symbol ?? '';
  if (
    !card &&
    asset.assetType === 'nft' &&
    asset.assetGroupId !== 'patron_nft' &&
    asset.nftIdentifier
  ) {
    assetName += ' #' + asset.nftIdentifier;
  }

  const assetDecimalPrecision = useMemo(() => {
    if (card || asset.assetType === 'nft') {
      return 0;
    }

    if (!priceData) return 6;

    return calculateAssetDecimalPrecision({
      tokenAmount: amount ?? '0',
      tokenDecimals: asset.decimals,
      rateUsd: priceData.usd.rate,
    });
  }, [amount, asset, priceData, card]);

  return (
    <div className={cn('bg-surfaceAlt flex gap-2 rounded-3xl p-4', className)}>
      <ProcessingAssetDisplay
        assetIcon={
          card ? (
            <CollectibleEmblem card={card} size="large" />
          ) : (
            <AssetIcon
              {...asset}
              asset={asset.assetType}
              chainAdornment={asset.chainAdornment || asset.chain}
            />
          )
        }
        assetName={assetName}
        chainLabel={`on ${getChainMetadata(asset.chain).name}`}
        amount={
          <FormattedNumber
            value={parseFloat(amount)}
            decimals={assetDecimalPrecision}
          />
        }
        usdDisplay={
          <>
            <span className="text-secondary">
              <span>(at </span>
              <FormattedPrice
                value={priceData?.usd.rate ?? 0}
                prefixDollarSign={true}
              />
              <span>)</span>
            </span>
            <FormattedPrice
              value={(priceData?.usd.rate ?? 0) * parseFloat(amount)}
              prefixDollarSign={true}
              className="text-emphasis"
            />
          </>
        }
      />
    </div>
  );
};
